import api from 'services/api';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => ({
  data_inicio: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
  data_fim: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0))
});

const getTodayLocalDate = () => formatLocalDate(new Date());

class ContratosService {
  normalizarNumero(valor) {
    if (valor === null || valor === undefined || valor === '') {
      return 0;
    }

    if (typeof valor === 'string' && valor.includes(',')) {
      return Number(valor.replace(/\./g, '').replace(',', '.')) || 0;
    }

    return Number(valor) || 0;
  }

  parseIntComAlias(obj, aliases) {
    for (const alias of aliases) {
      const valor = obj?.[alias];
      if (valor !== undefined && valor !== null && valor !== '') {
        return parseInt(valor, 10) || 0;
      }
    }

    return 0;
  }

  normalizarChavePlano(plano = {}) {
    if (plano.idPlano !== null && plano.idPlano !== undefined && plano.idPlano !== '') {
      return `id:${String(plano.idPlano)}`;
    }

    const nomeNormalizado = String(plano.nome || '')
      .trim()
      .toLowerCase();

    return `nome:${nomeNormalizado || 'plano-nao-informado'}`;
  }

  consolidarPlanos(planos = []) {
    const camposNumericos = [
      'totalContratos',
      'contratosPf',
      'contratosPj',
      'faturamentoPf',
      'faturamentoPj',
      'faturamentoTotal',
      'ativos',
      'suspensos',
      'internetOk',
      'internetProblema',
      'contratosBloqueioManual',
      'valorBloqueioManual',
      'contratosEmitidos',
      'titulosEmitidos',
      'valorEmitido',
      'valorAteVencimento',
      'descontoTotal',
      'contratosRecebidos',
      'titulosRecebidos',
      'valorRecebido',
      'contratosEmitidosPf',
      'titulosEmitidosPf',
      'valorEmitidoPf',
      'contratosRecebidosPf',
      'titulosRecebidosPf',
      'valorRecebidoPf',
      'contratosEmitidosPj',
      'titulosEmitidosPj',
      'valorEmitidoPj',
      'contratosRecebidosPj',
      'titulosRecebidosPj',
      'valorRecebidoPj'
    ];

    const camposEspelhados = {
      contratosBloqueioManual: 'contratos_bloqueio_manual',
      valorBloqueioManual: 'valor_bloqueio_manual',
      contratosEmitidos: 'contratos_emitidos',
      titulosEmitidos: 'titulos_emitidos',
      valorEmitido: 'valor_emitido',
      valorAteVencimento: 'valor_ate_vencimento',
      descontoTotal: 'desconto_total',
      contratosRecebidos: 'contratos_recebidos',
      titulosRecebidos: 'titulos_recebidos',
      valorRecebido: 'valor_recebido',
      contratosEmitidosPf: 'contratos_emitidos_pf',
      titulosEmitidosPf: 'titulos_emitidos_pf',
      valorEmitidoPf: 'valor_emitido_pf',
      contratosRecebidosPf: 'contratos_recebidos_pf',
      titulosRecebidosPf: 'titulos_recebidos_pf',
      valorRecebidoPf: 'valor_recebido_pf',
      contratosEmitidosPj: 'contratos_emitidos_pj',
      titulosEmitidosPj: 'titulos_emitidos_pj',
      valorEmitidoPj: 'valor_emitido_pj',
      contratosRecebidosPj: 'contratos_recebidos_pj',
      titulosRecebidosPj: 'titulos_recebidos_pj',
      valorRecebidoPj: 'valor_recebido_pj'
    };

    const acumulados = new Map();

    planos.forEach((planoAtual) => {
      const plano = this.adaptarPlano(planoAtual);
      const chave = this.normalizarChavePlano(plano);
      const existente = acumulados.get(chave);

      if (!existente) {
        const base = { ...plano };

        camposNumericos.forEach((campo) => {
          base[campo] = this.normalizarNumero(base[campo]);
        });

        Object.entries(camposEspelhados).forEach(([campoPrincipal, campoEspelhado]) => {
          base[campoEspelhado] = base[campoPrincipal];
        });

        acumulados.set(chave, base);
        return;
      }

      camposNumericos.forEach((campo) => {
        existente[campo] = this.normalizarNumero(existente[campo]) + this.normalizarNumero(plano[campo]);
      });

      Object.entries(camposEspelhados).forEach(([campoPrincipal, campoEspelhado]) => {
        existente[campoEspelhado] = existente[campoPrincipal];
      });
    });

    return Array.from(acumulados.values());
  }

  adaptarPlano(planoApi) {
    const plano = planoApi || {};
    const contratosBloqueioManual = parseInt(plano.contratos_bloqueio_manual ?? plano.contratosBloqueioManual ?? 0, 10) || 0;
    const valorBloqueioManual = this.normalizarNumero(plano.valor_bloqueio_manual ?? plano.valorBloqueioManual);
    const contratosEmitidos = this.parseIntComAlias(plano, ['contratos_emitidos', 'contratosEmitidos']);
    const titulosEmitidos = this.parseIntComAlias(plano, ['titulos_emitidos', 'titulosEmitidos']);
    const valorEmitido = this.normalizarNumero(plano.valor_emitido ?? plano.valorEmitido);
    const valorAteVencimento = this.normalizarNumero(plano.valor_ate_vencimento ?? plano.valorAteVencimento);
    const descontoTotal = this.normalizarNumero(plano.desconto_total ?? plano.descontoTotal);
    const contratosRecebidos = this.parseIntComAlias(plano, ['contratos_recebidos', 'contratosRecebidos']);
    const titulosRecebidos = this.parseIntComAlias(plano, ['titulos_recebidos', 'titulosRecebidos']);
    const valorRecebido = this.normalizarNumero(plano.valor_recebido ?? plano.valorRecebido);
    const contratosEmitidosPf = this.parseIntComAlias(plano, ['contratos_emitidos_pf', 'contratosEmitidosPf']);
    const titulosEmitidosPf = this.parseIntComAlias(plano, ['titulos_emitidos_pf', 'titulosEmitidosPf']);
    const valorEmitidoPf = this.normalizarNumero(plano.valor_emitido_pf ?? plano.valorEmitidoPf);
    const contratosRecebidosPf = this.parseIntComAlias(plano, ['contratos_recebidos_pf', 'contratosRecebidosPf']);
    const titulosRecebidosPf = this.parseIntComAlias(plano, ['titulos_recebidos_pf', 'titulosRecebidosPf']);
    const valorRecebidoPf = this.normalizarNumero(plano.valor_recebido_pf ?? plano.valorRecebidoPf);
    const contratosEmitidosPj = this.parseIntComAlias(plano, ['contratos_emitidos_pj', 'contratosEmitidosPj']);
    const titulosEmitidosPj = this.parseIntComAlias(plano, ['titulos_emitidos_pj', 'titulosEmitidosPj']);
    const valorEmitidoPj = this.normalizarNumero(plano.valor_emitido_pj ?? plano.valorEmitidoPj);
    const contratosRecebidosPj = this.parseIntComAlias(plano, ['contratos_recebidos_pj', 'contratosRecebidosPj']);
    const titulosRecebidosPj = this.parseIntComAlias(plano, ['titulos_recebidos_pj', 'titulosRecebidosPj']);
    const valorRecebidoPj = this.normalizarNumero(plano.valor_recebido_pj ?? plano.valorRecebidoPj);

    return {
      idPlano: plano.id_plano ?? plano.idPlano ?? plano.id ?? null,
      nome: plano.vd_contratos_nome ?? plano.nome ?? plano.plano ?? 'Plano não informado',
      totalContratos: parseInt(plano.total_contratos ?? plano.totalContratos ?? 0, 10) || 0,
      contratosPf: parseInt(plano.contratos_pf ?? plano.contratosPf ?? 0, 10) || 0,
      contratosPj: parseInt(plano.contratos_pj ?? plano.contratosPj ?? 0, 10) || 0,
      faturamentoPf: this.normalizarNumero(plano.faturamento_pf ?? plano.faturamentoPf),
      faturamentoPj: this.normalizarNumero(plano.faturamento_pj ?? plano.faturamentoPj),
      faturamentoTotal: this.normalizarNumero(
        plano.faturamento_total ??
        plano.faturamentoTotal ??
        (this.normalizarNumero(plano.faturamento_pf ?? plano.faturamentoPf) + this.normalizarNumero(plano.faturamento_pj ?? plano.faturamentoPj))
      ),
      ativos: parseInt(plano.ativos ?? 0, 10) || 0,
      suspensos: parseInt(plano.suspensos ?? 0, 10) || 0,
      internetOk: parseInt(plano.internetOk ?? plano.internet_ok ?? 0, 10) || 0,
      internetProblema: parseInt(plano.internetProblema ?? plano.internet_problema ?? 0, 10) || 0,
      contratosBloqueioManual,
      valorBloqueioManual,
      contratosEmitidos,
      titulosEmitidos,
      valorEmitido,
      valorAteVencimento,
      descontoTotal,
      contratosRecebidos,
      titulosRecebidos,
      valorRecebido,
      contratosEmitidosPf,
      titulosEmitidosPf,
      valorEmitidoPf,
      contratosRecebidosPf,
      titulosRecebidosPf,
      valorRecebidoPf,
      contratosEmitidosPj,
      titulosEmitidosPj,
      valorEmitidoPj,
      contratosRecebidosPj,
      titulosRecebidosPj,
      valorRecebidoPj,
      contratos_bloqueio_manual: contratosBloqueioManual,
      valor_bloqueio_manual: valorBloqueioManual,
      contratos_emitidos: contratosEmitidos,
      titulos_emitidos: titulosEmitidos,
      valor_emitido: valorEmitido,
      valor_ate_vencimento: valorAteVencimento,
      desconto_total: descontoTotal,
      contratos_recebidos: contratosRecebidos,
      titulos_recebidos: titulosRecebidos,
      valor_recebido: valorRecebido,
      contratos_emitidos_pf: contratosEmitidosPf,
      titulos_emitidos_pf: titulosEmitidosPf,
      valor_emitido_pf: valorEmitidoPf,
      contratos_recebidos_pf: contratosRecebidosPf,
      titulos_recebidos_pf: titulosRecebidosPf,
      valor_recebido_pf: valorRecebidoPf,
      contratos_emitidos_pj: contratosEmitidosPj,
      titulos_emitidos_pj: titulosEmitidosPj,
      valor_emitido_pj: valorEmitidoPj,
      contratos_recebidos_pj: contratosRecebidosPj,
      titulos_recebidos_pj: titulosRecebidosPj,
      valor_recebido_pj: valorRecebidoPj
    };
  }

  adaptarCidade(cidadeApi) {
    const cidade = cidadeApi || {};
    const planos = Array.isArray(cidade.planos)
      ? cidade.planos.map((plano) => this.adaptarPlano(plano))
      : [];

    return {
      idCidade: cidade.idCidade ?? cidade.id_cidade ?? null,
      cidadeNome: cidade.cidadeNome ?? cidade.cidade_nome ?? cidade.nome ?? 'Cidade não informada',
      totalContratos: parseInt(cidade.totalContratos ?? cidade.total_contratos ?? 0, 10) || 0,
      valorTotal: this.normalizarNumero(cidade.valorTotal ?? cidade.valor_total),
      ativos: parseInt(cidade.ativos ?? 0, 10) || 0,
      suspensos: parseInt(cidade.suspensos ?? 0, 10) || 0,
      pessoasFisicas: parseInt(cidade.pessoasFisicas ?? cidade.pessoas_fisicas ?? 0, 10) || 0,
      pessoasJuridicas: parseInt(cidade.pessoasJuridicas ?? cidade.pessoas_juridicas ?? 0, 10) || 0,
      faturamentoPessoaFisica: this.normalizarNumero(
        cidade.faturamentoPessoaFisica ?? cidade.faturamento_pessoa_fisica
      ),
      faturamentoPessoaJuridica: this.normalizarNumero(
        cidade.faturamentoPessoaJuridica ?? cidade.faturamento_pessoa_juridica
      ),
      contratosBloqueioManual: parseInt(cidade.contratosBloqueioManual ?? cidade.contratos_bloqueio_manual ?? 0, 10) || 0,
      valorBloqueioManual: this.normalizarNumero(cidade.valorBloqueioManual ?? cidade.valor_bloqueio_manual),
      titulosEmitidos: this.parseIntComAlias(cidade, ['titulos_emitidos', 'titulosEmitidos']),
      contratosEmitidos: this.parseIntComAlias(cidade, ['contratos_emitidos', 'contratosEmitidos']),
      valorEmitido: this.normalizarNumero(cidade.valorEmitido ?? cidade.valor_emitido),
      valorAteVencimento: this.normalizarNumero(cidade.valorAteVencimento ?? cidade.valor_ate_vencimento),
      descontoTotal: this.normalizarNumero(cidade.descontoTotal ?? cidade.desconto_total),
      titulosRecebidos: this.parseIntComAlias(cidade, ['titulos_recebidos', 'titulosRecebidos']),
      contratosRecebidos: this.parseIntComAlias(cidade, ['contratos_recebidos', 'contratosRecebidos']),
      valorRecebido: this.normalizarNumero(cidade.valorRecebido ?? cidade.valor_recebido),
      titulosEmitidosPf: this.parseIntComAlias(cidade, ['titulos_emitidos_pf', 'titulosEmitidosPf']),
      contratosEmitidosPf: this.parseIntComAlias(cidade, ['contratos_emitidos_pf', 'contratosEmitidosPf']),
      valorEmitidoPf: this.normalizarNumero(cidade.valorEmitidoPf ?? cidade.valor_emitido_pf),
      titulosRecebidosPf: this.parseIntComAlias(cidade, ['titulos_recebidos_pf', 'titulosRecebidosPf']),
      contratosRecebidosPf: this.parseIntComAlias(cidade, ['contratos_recebidos_pf', 'contratosRecebidosPf']),
      valorRecebidoPf: this.normalizarNumero(cidade.valorRecebidoPf ?? cidade.valor_recebido_pf),
      titulosEmitidosPj: this.parseIntComAlias(cidade, ['titulos_emitidos_pj', 'titulosEmitidosPj']),
      contratosEmitidosPj: this.parseIntComAlias(cidade, ['contratos_emitidos_pj', 'contratosEmitidosPj']),
      valorEmitidoPj: this.normalizarNumero(cidade.valorEmitidoPj ?? cidade.valor_emitido_pj),
      titulosRecebidosPj: this.parseIntComAlias(cidade, ['titulos_recebidos_pj', 'titulosRecebidosPj']),
      contratosRecebidosPj: this.parseIntComAlias(cidade, ['contratos_recebidos_pj', 'contratosRecebidosPj']),
      valorRecebidoPj: this.normalizarNumero(cidade.valorRecebidoPj ?? cidade.valor_recebido_pj),
      planos
    };
  }

  /**
   * Adapta os dados da API para o formato esperado pelo frontend
   */
  adaptarContrato(contratoApi) {
    // Mapeamento de tipos de pessoa do N8N para o frontend
    const mapeamentoTipoPessoa = {
      'F': 'Física',
      'J': 'Jurídica', 
      'E': 'Estrangeiro'
    };

    return {
      id: contratoApi.contrato_id || 'N/A',
      cliente: contratoApi.razao || contratoApi.cliente || 'Cliente não informado',
      cnpj_cpf: contratoApi.cnpj_cpf || 'CPF/CNPJ não informado',
      cidade: contratoApi.cidade || 'Cidade não informada',
      email: contratoApi.cliente_email || 'Email não informado',
      plano: contratoApi.contrato_plano || 'Plano não informado',
      valor_contrato: parseFloat(contratoApi.valor_contrato || contratoApi.valor_plano || 0),
      data_ativacao: contratoApi.data_ativacao || 'Data não informada',
      celular: contratoApi.celular || 'Celular não informado',
      tipo_pessoa: mapeamentoTipoPessoa[contratoApi.tipo_pessoa] || contratoApi.tipo_pessoa || 'Tipo não informado',
      status_cliente: contratoApi.status_cliente || 'N',
      data_assinatura: contratoApi.data_assinatura || null,
      data_renovacao: contratoApi.data_renovacao || null,
      status_contrato: contratoApi.status_contrato || contratoApi.contrato_status || 'I',
      status_internet: contratoApi.status_internet || 'SS'
    };
  }

  /**
   * Adapta o resumo da API para o formato esperado pelo frontend
  */
  adaptarResumo(resumoApi) {
    const resumo = Array.isArray(resumoApi) ? (resumoApi[0] || {}) : (resumoApi || {});
    const cidades = Array.isArray(resumo.cidades)
      ? resumo.cidades.map((cidade) => this.adaptarCidade(cidade))
      : [];
    const planosNormalizados = Array.isArray(resumo.planos)
      ? this.consolidarPlanos(resumo.planos)
      : this.consolidarPlanos(cidades.flatMap((cidade) => cidade.planos || []));
    const faturamentoCpf = this.normalizarNumero(
      resumo.faturamentoCpf ??
      resumo.faturamento_cpf ??
      resumo.faturamentoPessoaFisica ??
      resumo.faturamento_pessoa_fisica ??
      0
    );
    const faturamentoCnpj = this.normalizarNumero(
      resumo.faturamentoCnpj ??
      resumo.faturamento_cnpj ??
      resumo.faturamentoPessoaJuridica ??
      resumo.faturamento_pessoa_juridica ??
      0
    );
    const contratosBloqueioManual = parseInt(resumo.contratosBloqueioManual ?? resumo.contratos_bloqueio_manual ?? 0, 10) || 0;
    const valorBloqueioManual = this.normalizarNumero(resumo.valorBloqueioManual ?? resumo.valor_bloqueio_manual);
    const titulosEmitidos = this.parseIntComAlias(resumo, ['titulos_emitidos', 'titulosEmitidos']);
    const contratosEmitidos = this.parseIntComAlias(resumo, ['contratos_emitidos', 'contratosEmitidos']);
    const valorEmitido = this.normalizarNumero(resumo.valorEmitido ?? resumo.valor_emitido);
    const valorAteVencimento = this.normalizarNumero(resumo.valorAteVencimento ?? resumo.valor_ate_vencimento);
    const descontoTotal = this.normalizarNumero(resumo.descontoTotal ?? resumo.desconto_total);
    const titulosRecebidos = this.parseIntComAlias(resumo, ['titulos_recebidos', 'titulosRecebidos']);
    const contratosRecebidos = this.parseIntComAlias(resumo, ['contratos_recebidos', 'contratosRecebidos']);
    const valorRecebido = this.normalizarNumero(resumo.valorRecebido ?? resumo.valor_recebido);
    const titulosEmitidosPf = this.parseIntComAlias(resumo, ['titulos_emitidos_pf', 'titulosEmitidosPf']);
    const contratosEmitidosPf = this.parseIntComAlias(resumo, ['contratos_emitidos_pf', 'contratosEmitidosPf']);
    const valorEmitidoPf = this.normalizarNumero(resumo.valorEmitidoPf ?? resumo.valor_emitido_pf);
    const titulosRecebidosPf = this.parseIntComAlias(resumo, ['titulos_recebidos_pf', 'titulosRecebidosPf']);
    const contratosRecebidosPf = this.parseIntComAlias(resumo, ['contratos_recebidos_pf', 'contratosRecebidosPf']);
    const valorRecebidoPf = this.normalizarNumero(resumo.valorRecebidoPf ?? resumo.valor_recebido_pf);
    const titulosEmitidosPj = this.parseIntComAlias(resumo, ['titulos_emitidos_pj', 'titulosEmitidosPj']);
    const contratosEmitidosPj = this.parseIntComAlias(resumo, ['contratos_emitidos_pj', 'contratosEmitidosPj']);
    const valorEmitidoPj = this.normalizarNumero(resumo.valorEmitidoPj ?? resumo.valor_emitido_pj);
    const titulosRecebidosPj = this.parseIntComAlias(resumo, ['titulos_recebidos_pj', 'titulosRecebidosPj']);
    const contratosRecebidosPj = this.parseIntComAlias(resumo, ['contratos_recebidos_pj', 'contratosRecebidosPj']);
    const valorRecebidoPj = this.normalizarNumero(resumo.valorRecebidoPj ?? resumo.valor_recebido_pj);
    const valorTotal = this.normalizarNumero(resumo.valorTotal ?? resumo.valor_total ?? (faturamentoCpf + faturamentoCnpj));

    return {
      totalContratos: parseInt(resumo.totalContratos ?? resumo.total_contratos) || 0,
      valorTotal,
      faturamentoCpf,
      faturamentoCnpj,
      faturamentoPessoaFisica: faturamentoCpf,
      faturamentoPessoaJuridica: faturamentoCnpj,
      ativos: parseInt(resumo.ativos) || 0,
      suspensos: parseInt(resumo.suspensos) || 0,
      internetOk: parseInt(resumo.internetOk) || 0,
      internetProblema: parseInt(resumo.internetProblema) || 0,
      pessoasFisicas: parseInt(resumo.pessoasFisicas ?? resumo.pessoas_fisicas) || 0,
      pessoasJuridicas: parseInt(resumo.pessoasJuridicas ?? resumo.pessoas_juridicas) || 0,
      contratosBloqueioManual,
      valorBloqueioManual,
      titulosEmitidos,
      contratosEmitidos,
      valorEmitido,
      valorAteVencimento,
      descontoTotal,
      titulosRecebidos,
      contratosRecebidos,
      valorRecebido,
      titulosEmitidosPf,
      contratosEmitidosPf,
      valorEmitidoPf,
      titulosRecebidosPf,
      contratosRecebidosPf,
      valorRecebidoPf,
      titulosEmitidosPj,
      contratosEmitidosPj,
      valorEmitidoPj,
      titulosRecebidosPj,
      contratosRecebidosPj,
      valorRecebidoPj,
      cidades,
      planos: planosNormalizados,
      data_fim: resumo.data_fim || getTodayLocalDate()
    };
  }

  /**
   * Calcula o resumo dos contratos (fallback local)
   */
  calcularResumo(contratos) {
    const resumo = {
      totalContratos: contratos.length,
      valorTotal: 0,
      faturamentoCpf: 0,
      faturamentoCnpj: 0,
      faturamentoPessoaFisica: 0,
      faturamentoPessoaJuridica: 0,
      ativos: 0,
      suspensos: 0,
      internetOk: 0,
      internetProblema: 0,
      pessoasFisicas: 0,
      pessoasJuridicas: 0,
      contratosBloqueioManual: 0,
      valorBloqueioManual: 0,
      titulosEmitidos: 0,
      valorEmitido: 0,
      valorAteVencimento: 0,
      descontoTotal: 0,
      titulosRecebidos: 0,
      valorRecebido: 0,
      titulosEmitidosPf: 0,
      valorEmitidoPf: 0,
      titulosRecebidosPf: 0,
      valorRecebidoPf: 0,
      titulosEmitidosPj: 0,
      valorEmitidoPj: 0,
      titulosRecebidosPj: 0,
      valorRecebidoPj: 0,
      planos: [],
      data_fim: getTodayLocalDate()
    };

    contratos.forEach(contrato => {
      // Soma o valor total dos contratos
      const valorContrato = contrato.valor_contrato || 0;
      resumo.valorTotal += valorContrato;

      // Status do contrato: A = Ativo, D = Desativado, CM = Bloqueio Manual, CA = Bloqueio Automático, FA = Financeiro em atraso, AA = Aguardando Assinatura
      if (contrato.status_contrato === 'A') {
        resumo.ativos++;
      } else if (['D', 'CM', 'CA', 'FA'].includes(contrato.status_contrato)) {
        // Desativado, Bloqueio Manual, Bloqueio Automático, Financeiro em atraso = Suspensos
        resumo.suspensos++;
      } else if (contrato.status_contrato === 'AA') {
        // Aguardando Assinatura não conta para ativos nem suspensos
      } else {
        // Para valores antigos ou desconhecidos, considerar como suspenso
        resumo.suspensos++;
      }

      // Status da internet: S = com conexão, N = sem conexão, SS = sem status
      if (contrato.status_internet === 'S') {
        resumo.internetOk++;
      } else if (contrato.status_internet === 'N') {
        resumo.internetProblema++;
      } else if (contrato.status_internet === 'SS') {
        // Sem status não conta para nenhum dos dois
      } else {
        // Para valores antigos ou desconhecidos, considerar como problema
        resumo.internetProblema++;
      }

      // Tipo de pessoa (usando valores mapeados)
      if (contrato.tipo_pessoa === 'Física') {
        resumo.pessoasFisicas++;
        resumo.faturamentoCpf += valorContrato;
        resumo.faturamentoPessoaFisica += valorContrato;
      } else if (contrato.tipo_pessoa === 'Jurídica') {
        resumo.pessoasJuridicas++;
        resumo.faturamentoCnpj += valorContrato;
        resumo.faturamentoPessoaJuridica += valorContrato;
      } else if (contrato.tipo_pessoa === 'Estrangeiro') {
        if (!resumo.pessoasEstrangeiras) {
          resumo.pessoasEstrangeiras = 0;
        }
        resumo.pessoasEstrangeiras++;
      }
    });

    return resumo;
  }

  /**
   * Busca contratos com filtros
   */
  async buscarContratos(filtros = {}) {
    try {
      const dateRange = getDefaultDateRange();
      const params = {
        empresa: filtros.empresa || 'MOV Fibra',
        status: filtros.status || 'A',
        cidade: filtros.cidade || 'todas',
        tipo_pessoa: filtros.tipo_pessoa || 'todos',
        data_inicio: filtros.data_inicio || dateRange.data_inicio,
        data_fim: filtros.data_fim || dateRange.data_fim,
        limite: filtros.limite || 25,
        offset: filtros.offset || 0
      };

      

      const response = await api.post('/n8n/contratos', params);
      
      if (response.data) {
        const contratosAdaptados = response.data.contratos?.map(contrato => 
          this.adaptarContrato(contrato)
        ) || [];

        // Usar resumo da API se disponível, senão calcular localmente
        let resumo;
        try {
          const resumoResponse = await this.buscarResumoContratos(filtros);
          resumo = this.adaptarResumo(resumoResponse.resumo);
        } catch (error) {
          console.warn('⚠️ Erro ao buscar resumo da API, calculando localmente:', error);
          resumo = this.calcularResumo(contratosAdaptados);
        }

        return {
          success: true,
          contratos: contratosAdaptados,
          resumo: resumo,
          total: response.data.total,
          limite: response.data.limite,
          offset: response.data.offset
        };
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar contratos:', error);
      throw error;
    }
  }

  /**
   * Busca resumo de contratos
   */
  async buscarResumoContratos(filtros = {}) {
    try {
      const dateRange = getDefaultDateRange();
      const params = {
        empresa: filtros.empresa || 'MOV Fibra',
        status: filtros.status || 'A',
        cidade: filtros.cidade || 'todas',
        tipo_pessoa: filtros.tipo_pessoa || 'todos',
        data_inicio: filtros.data_inicio || dateRange.data_inicio,
        data_fim: filtros.data_fim || dateRange.data_fim
      };

      

      const response = await api.post('/n8n/contratos/resumo', params);
      
      if (response.data) {
        return {
          success: true,
          resumo: this.adaptarResumo(response.data)
        };
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar resumo de contratos:', error);
      throw error;
    }
  }

  /**
   * Busca lista de cidades
   */
  async buscarCidades(empresa = 'MOV Fibra') {
    try {
      const response = await api.post('/n8n/contratos/cidades', { empresa });
      
      if (response.data && response.data.cidades) {
        // Processar a nova estrutura da API
        const cidadesProcessadas = response.data.cidades
          .map(item => item.cliente_contrato_cidade_nome)
          .filter(cidade => cidade !== null && cidade !== undefined && cidade !== '')
          .sort((a, b) => a.localeCompare(b, 'pt-BR')); // Ordenar alfabeticamente

        return {
          success: true,
          cidades: cidadesProcessadas
        };
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar cidades:', error);
      throw error;
    }
  }
}

const contratosService = new ContratosService();
export default contratosService;
